import { ApplicationVersion, CountrySelector } from '@/micro/types/version.type'
import { APP_ENVIRONMENT } from '@/micro/utils/environment'

const DEFAULT_COUNTRY = 'colombia'

const VALID_COUNTRIES = [
  'colombia',
  'mexico',
  'argentina',
  'republicaDominicana',
  'costaRica',
  'peru',
  'panama',
  'spain',
  'venezuela',
]

const COUNTRY_ALIAS: Record<string, ApplicationVersion> = {
  'republica-dominicana': 'republicaDominicana',
  republicadominicana: 'republicaDominicana',
  rdominicana: 'republicaDominicana',
  'costa-rica': 'costaRica',
  costarica: 'costaRica',
}

const COUNTRIES_SELECTOR: Record<ApplicationVersion, CountrySelector> = {
  argentina: {
    id: 'argentina',
    title: 'Argentina',
  },
  colombia: {
    id: 'colombia',
    title: 'Colombia',
  },
  costaRica: {
    id: 'costaRica',
    title: 'Costa Rica',
  },
  spain: {
    id: 'spain',
    title: 'España',
  },
  mexico: {
    id: 'mexico',
    title: 'México',
  },
  panama: {
    id: 'panama',
    title: 'Panamá',
  },
  peru: {
    id: 'peru',
    title: 'Perú',
  },
  republicaDominicana: {
    id: 'republicaDominicana',
    title: 'R. Dominicana',
  },
  venezuela: {
    id: 'venezuela',
    title: 'Venezuela',
  },
}

const PRODUCT_NAME_MAPPING: Record<ApplicationVersion, string> = {
  argentina: 'Alegra Gestión',
  colombia: 'Alegra Contabilidad',
  costaRica: 'Alegra Contabilidad',
  spain: 'Alegra Gestión',
  mexico: 'Alegra Contabilidad',
  panama: 'Alegra Contabilidad',
  peru: 'Alegra Contabilidad',
  republicaDominicana: 'Alegra Contabilidad',
  venezuela: 'Alegra Contabilidad',
}

// Países disponibles para Nómina en producción
const PAYROLL_COUNTRIES: ApplicationVersion[] = ['colombia', 'republicaDominicana']

// Países adicionales para Nómina en testing
const PAYROLL_COUNTRIES_TESTING: ApplicationVersion[] = ['mexico']

// Países con UI ICP de Nómina en producción
const NOMINA_ICP_COUNTRIES: ApplicationVersion[] = ['republicaDominicana']

// Países adicionales con UI ICP de Nómina en testing/local
const NOMINA_ICP_COUNTRIES_TESTING: ApplicationVersion[] = ['colombia', 'mexico']

// Países NO disponibles para POS (Venezuela solo disponible por parámetro explícito)
const POS_EXCLUDED_COUNTRIES: ApplicationVersion[] = ['venezuela']

// Rutas de Nómina
const PAYROLL_ROUTES = [
  'register-payroll',
  'register-payroll-by-country',
]

// Rutas de POS
const POS_ROUTES = ['register-pos', 'register-tpv', 'register-pv']

/**
 * Obtiene los países válidos para Nómina según el ambiente
 * @param environment - Ambiente actual (local, testing, sandbox, production)
 * @returns Array de países válidos para Nómina
 */
const isNominaIcpTestingEnvironment = (environment: string): boolean =>
  environment === 'testing' || environment === 'local'

const getPayrollValidCountries = (environment: string): ApplicationVersion[] => {
  const validCountries = [...PAYROLL_COUNTRIES]

  if (isNominaIcpTestingEnvironment(environment)) {
    validCountries.push(...PAYROLL_COUNTRIES_TESTING)
  }

  return validCountries
}

const getNominaIcpCountries = (environment: string = APP_ENVIRONMENT): ApplicationVersion[] => {
  const countries = [...NOMINA_ICP_COUNTRIES]

  if (isNominaIcpTestingEnvironment(environment)) {
    countries.push(...NOMINA_ICP_COUNTRIES_TESTING)
  }

  return countries
}

const isNominaIcpRegisterCountry = (
  country: ApplicationVersion,
  environment: string = APP_ENVIRONMENT,
): boolean => getNominaIcpCountries(environment).includes(country)

/**
 * Verifica si un país es válido para POS
 * @param country - País a validar
 * @returns true si el país es válido para POS
 */
const isPOSValidCountry = (country: ApplicationVersion): boolean => {
  return !POS_EXCLUDED_COUNTRIES.includes(country)
}

export {
  DEFAULT_COUNTRY,
  VALID_COUNTRIES,
  COUNTRY_ALIAS,
  COUNTRIES_SELECTOR,
  PRODUCT_NAME_MAPPING,
  PAYROLL_COUNTRIES,
  PAYROLL_COUNTRIES_TESTING,
  NOMINA_ICP_COUNTRIES,
  NOMINA_ICP_COUNTRIES_TESTING,
  PAYROLL_ROUTES,
  POS_ROUTES,
  POS_EXCLUDED_COUNTRIES,
  getPayrollValidCountries,
  getNominaIcpCountries,
  isNominaIcpRegisterCountry,
  isPOSValidCountry,
}
